import { useEffect, useMemo, useState } from 'react';
import { Delete, Description, MoreVert, Visibility } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { CitationCardProps } from './CitationCard.props';
import {
  citationTextBoxSx,
  citationTextSx,
  deleteIconSx,
  deleteMenuItemSx,
  dividerSx,
  documentInfoStackSx,
  documentInfoTextSx,
  footerCaptionSx,
  headerTitleSx,
  iconButtonSx,
} from './CitationCard.styles';
import { DocumentInfo } from './CitationCard.types';
import CitationService from '../../../services/citations';
import FilesService from '../../../services/documents';
import { Logger } from '../../../utilities';

const CitationCard = ({ citation, identifier, onDelete, onView }: CitationCardProps) => {
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const filesService = useMemo(() => new FilesService(), []);
  const citationService = useMemo(() => new CitationService(), []);

  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (citation.document_reference) {
        try {
          const response = await filesService.getDocumentById(String(citation.document_reference));
          setDocumentInfo(response as DocumentInfo);
        } catch (error) {
          console.error('Error fetching document info:', error);
        }
      }
    };
    fetchDocumentInfo();
  }, [citation.document_reference, filesService]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const createdByName = citation.created_by
    ? `${citation.created_by.first_name} ${citation.created_by.last_name}`
    : 'Unknown User';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!citation.id) {
      enqueueSnackbar('Citation ID not found', { variant: 'error' });
      return;
    }

    setIsDeleting(true);
    try {
      await citationService.deleteCitation(citation.id);
      enqueueSnackbar('Citation deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);

      // Call the onDelete callback if provided
      if (onDelete) {
        onDelete(citation.id);
      }
    } catch (error) {
      Logger.error('Error deleting citation:', error);
      enqueueSnackbar('Failed to delete citation', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const handleView = (e: React.MouseEvent<HTMLElement>) => {
    stopPropagation(e);
    onView?.(citation);
  };

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" sx={headerTitleSx}>
          {identifier} - {citation.field_name}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <IconButton size="small" sx={iconButtonSx} onMouseDown={stopPropagation} onClick={handleView} aria-label="view citation">
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={iconButtonSx} onClick={handleMenuOpen} aria-label="citation options">
            <MoreVert fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <MenuItem onClick={handleDelete} sx={deleteMenuItemSx}>
          <Delete fontSize="small" sx={deleteIconSx} />
          Delete Citation
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description">
        <DialogTitle id="delete-dialog-title">Delete Citation?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this citation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider />

      {/* Document Info */}
      {documentInfo && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={documentInfoStackSx}>
          <Description sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
          <Typography variant="body2" sx={documentInfoTextSx}>
            {documentInfo.file.filename}.{documentInfo.file.file_type}
          </Typography>
        </Stack>
      )}

      {/* Citation Text */}
      {citation.tokenReferences.map((tokRef, i) => (
        <Box key={`${tokRef.page}-${tokRef.token_ids.join(',')}-${i}`} sx={citationTextBoxSx}>
          <Typography
            variant="body2"
            sx={citationTextSx}>
            "{tokRef.plain_text}"
          </Typography>
        </Box>
      ))}

      <Divider sx={dividerSx} />

      {/* Footer */}
      <Typography
        variant="caption"
        sx={footerCaptionSx}>
        Cited by {createdByName} - {citation.created_at ? formatDate(citation.created_at) : 'N/A'}
      </Typography>
    </Stack>
  );
};

export default CitationCard;
